import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Facebook, Menu, X, Info, MessageCircle } from "lucide-react";
import { BIZ, HIGHLIGHTS, PRODUCTS, STEPS } from "./belcinaData";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const MAROON = "#6E1F2A";
const CREAM = "#F3EEE4";

const fadeUp = { initial: { opacity: 0, y: 22 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, ease: "easeOut" } };

export default function BelcinaSite() {
  const [open, setOpen] = useState(false);
  const links = [["About", "#about"], ["Jewelry", "#jewelry"], ["Visit", "#visit"]];

  return (
    <div id="top" className="min-h-screen bg-white" style={{ color: "#241416", fontFamily: "'Barlow', sans-serif" }} data-testid="belcina-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Libre+Caslon+Display&family=Barlow:wght@400;500;600;700&display=swap'); .bc-caslon{font-family:'Libre Caslon Display',serif;}`}</style>

      <div data-testid="jewelry-disclaimer" className="bg-black text-white text-center text-xs py-2.5">
        <span className="inline-flex items-center gap-2"><Info size={13} style={{ color: "#D9A0A8" }} />Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {BIZ.name}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-white border-b-2" style={{ borderColor: MAROON }}>
        <nav className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="belcina-logo" className="bc-caslon text-xl" style={{ color: MAROON }}>Belciña Jewelry</a>
          <div className="hidden md:flex items-center gap-8">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-black/70 hover:text-black">{l}</a>)}
          </div>
          <a href="#visit" data-testid="belcina-header-cta" className="hidden sm:inline-flex px-6 py-2.5 text-sm font-semibold text-white" style={{ background: MAROON }}>Visit Us</a>
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="belcina-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="md:hidden px-5 pb-4 space-y-1 bg-white">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium">{l}</a>)}</div>}
      </header>

      {/* HERO — the real art-deco landmark building */}
      <section className="relative min-h-[86vh] flex items-end" data-testid="belcina-hero">
        <img src="/belcina/storefront-day.jpg" alt="Belciña Jewelry, Inc. — the art-deco landmark building in downtown Dumaguete" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 15%" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(20,8,10,0.1) 0%, rgba(20,8,10,0.94) 82%)" }} />
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-16 w-full">
          <motion.div {...fadeUp}>
            <span className="text-xs uppercase tracking-[0.25em]" style={{ color: "#E2A2AC" }}>Downtown Dumaguete City</span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl bc-caslon text-white leading-[1.1]">"{BIZ.tagline.replace(/"/g, "")}"</h1>
            <p className="mt-5 text-white/80 max-w-xl">A recognizable Dumaguete landmark, trusted for decades of jewelry, watches and gold.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#jewelry" data-testid="belcina-hero-cta" className="px-8 py-3.5 text-sm font-semibold text-white" style={{ background: MAROON }}>Explore Our Jewelry</a>
              <a href={BIZ.facebookMain} target="_blank" rel="noopener noreferrer" data-testid="belcina-hero-message" className="px-8 py-3.5 text-sm font-semibold text-white border border-white/40">Message Us</a>
            </div>
            <p className="mt-7 text-sm text-white/60">{BIZ.rating}★ Rating · {BIZ.reviewCount} Reviews</p>
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
        <motion.p {...fadeUp} className="text-2xl sm:text-3xl bc-caslon leading-relaxed" style={{ color: MAROON }}>{BIZ.intro}</motion.p>
        <motion.div {...fadeUp} className="mt-12 grid sm:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x" style={{ borderColor: "#EAD8DA" }}>
          {HIGHLIGHTS.map((h) => (
            <div key={h.title} className="px-5 py-5">
              <p className="font-semibold text-sm">{h.title}</p>
              <p className="mt-1.5 text-xs text-black/60">{h.text}</p>
            </div>
          ))}
        </motion.div>
      </section>

      {/* JEWELRY */}
      <section id="jewelry" className="py-20" style={{ background: CREAM }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="bc-caslon text-4xl text-center mb-12" style={{ color: MAROON }}>Our Jewelry</motion.h2>
          <div className="grid sm:grid-cols-3 gap-8">
            {PRODUCTS.map((p) => (
              <motion.a key={p.name} {...fadeUp} href={BIZ.facebookMain} target="_blank" rel="noopener noreferrer" data-testid="belcina-product-card" className="bg-white shadow-sm group">
                <div className="overflow-hidden"><img src={p.img} alt={p.name} className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-5">
                  <h3 className="text-sm font-semibold">{p.name}</h3>
                  <p className="mt-1 text-xs text-black/60">{p.desc}</p>
                  <p className="mt-3 text-xs font-semibold" style={{ color: MAROON }}>Ask About This Piece &rarr;</p>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* STORE CARD — the clinic's own real signage/contact card */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-16 text-center">
        <motion.img {...fadeUp} src="/belcina/storefront-sign.jpg" alt="Belciña Jewelry, Inc. address and phone numbers" className="w-full shadow-md" />
      </section>

      {/* STEPS */}
      <section className="py-16" style={{ background: MAROON }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 grid sm:grid-cols-4 gap-6 text-center">
          {STEPS.map((s) => (
            <motion.div key={s.n} {...fadeUp}>
              <div className="bc-caslon text-2xl text-white">{s.n}</div>
              <p className="mt-2 font-semibold text-sm text-white">{s.title}</p>
              <p className="mt-1 text-xs text-white/60">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="max-w-6xl mx-auto px-5 sm:px-8 py-20 grid md:grid-cols-2 gap-12">
        <motion.div {...fadeUp}>
          <h2 className="bc-caslon text-3xl" style={{ color: MAROON }}>Visit Us</h2>
          <ul className="mt-7 space-y-5">
            <li className="flex items-start gap-4"><MapPin size={20} style={{ color: MAROON }} className="mt-0.5" />{BIZ.address}</li>
            <li className="flex items-start gap-4"><Phone size={20} style={{ color: MAROON }} className="mt-0.5" /><a href={`tel:${BIZ.phone.replace(/\s/g, "")}`} className="hover:underline">{BIZ.phone}</a></li>
            <li className="flex items-start gap-4"><Facebook size={20} style={{ color: MAROON }} className="mt-0.5" /><a href={BIZ.facebookMain} target="_blank" rel="noopener noreferrer" data-testid="belcina-facebook-link" className="hover:underline">bji21021</a></li>
          </ul>
        </motion.div>
        <motion.div {...fadeUp} className="min-h-[300px] shadow-md"><iframe title="Belcina Jewelry map" src={BIZ.mapEmbed} className="w-full h-full min-h-[300px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" /></motion.div>
      </section>

      <footer className="py-8 bg-white border-t-2" style={{ borderColor: MAROON }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-black/55">
          <p className="bc-caslon text-base" style={{ color: MAROON }}>Belciña Jewelry, Inc.</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {BIZ.name}. Confirm pricing in person.</p>
        </div>
      </footer>

      <a href={BIZ.facebookMain} target="_blank" rel="noopener noreferrer" data-testid="belcina-sticky-contact" className="md:hidden fixed bottom-4 inset-x-4 z-50 px-6 py-3.5 text-center text-sm font-semibold text-white shadow-xl flex items-center justify-center gap-2" style={{ background: MAROON }}><MessageCircle size={17} /> Message Belciña</a>
      <ConceptCTAPill slug="belcina-jewelry-concept" />
    </div>
  );
}
